import { seoCategories as defaultSeoCategories } from '../config/seo/seoCategories';
import { seoLocations, JAKARTA_LOC_L1, findSeoLocationBySlug } from '../config/seo/seoLocations';
import { genericTemplates, resolveLocationLabel } from '../config/seo/seoMetadataTemplates';

// ================ Slug <-> combo resolution ================ //

// The build-time bundled list (src/config/seo/seoCategories.json) is the
// default/fallback — every function below that needs the current category
// list takes an explicit `categories` param instead, defaulting to this.
// The *live* list (L2/L3 auto-detected from Sharetribe + the keyword/CAT4
// admin-editable files, see server/api-util/seoTaxonomyMerge.js) reaches
// here the same way eligiblePaths already does: server/dataLoader.js (plain
// CommonJS, can't import this ESM module directly) dispatches it as plain
// data into the Redux store (`seoPages/setSeoCategories`, see
// src/ducks/seoPages.duck.js), and callers that have store access
// (SeoLandingPage.duck.js's thunk via getState(), SeoLandingPage.js and
// SeoLinks.js via useSelector) pass `state.seoPages.seoCategories` in
// explicitly. This keeps SSR — real page loads, the sitemap, redirects,
// meta tags — current with no rebuild/deploy; client-side in-app
// navigation uses whatever the store already has from the last SSR
// bootstrap.
export { defaultSeoCategories };

const categorySlugPart = category => category.canonicalSlug.replace(/^\/sewa-/, '');

/**
 * Every theoretically valid `/sewa-...` combo for a given category list,
 * keyed by the full path. Cached per categories array reference (a WeakMap,
 * not a single global) so repeated calls within the same request/render
 * reuse the same index cheaply, while a different categories array (a new
 * request's fresh live data, or the bundled default) gets its own.
 */
const buildComboIndex = categories => {
  const index = new Map();
  categories.forEach(category => {
    const slugPart = categorySlugPart(category);

    index.set(`/sewa-${slugPart}`, { category, location: null, scope: 'category-only' });

    seoLocations.forEach(location => {
      index.set(`/sewa-${slugPart}-${location.slug}`, { category, location, scope: 'locL2' });
    });

    index.set(`/sewa-${slugPart}-${JAKARTA_LOC_L1.slug}`, {
      category,
      location: JAKARTA_LOC_L1,
      scope: 'locL1',
    });
  });
  return index;
};

const comboIndexCache = new WeakMap(); // categories array -> Map(path -> combo)
const getComboIndex = categories => {
  if (!comboIndexCache.has(categories)) {
    comboIndexCache.set(categories, buildComboIndex(categories));
  }
  return comboIndexCache.get(categories);
};

/**
 * Resolves a `/sewa-:seoSlug` route param (the part after "/sewa-") into
 * its category/location combo, or null if it isn't a known combo.
 * Deterministic lookup only — no string-guessing (per SOW Section 3).
 * @param {string} seoSlug
 * @param {Array} [categories] defaults to the build-time bundled list
 */
export const resolveSeoSlug = (seoSlug, categories = defaultSeoCategories) => {
  return getComboIndex(categories).get(`/sewa-${seoSlug}`) || null;
};

// ================ Search params ================ //

/**
 * Builds the `pub_*`/`keywords` search params for a resolved combo — the
 * exact shape SearchPage.duck.js's loadData already spreads into the
 * Marketplace API query, so no new search logic is needed on the
 * SearchPage side. A CAT4 combo (level 'L4') has no Sharetribe category id
 * of its own — per the client, it searches/counts by free-text keyword
 * scoped to its linked category (see server/api/jobs/seoEligibility.job.js's
 * filtersFor(), the same shape mirrored here for the client-facing search).
 */
export const buildSeoSearchParams = ({ category, location, scope }) => {
  const params =
    category.level === 'L4'
      ? {
          keywords: category.keywordTerm,
          ...(category.linkedCategoryId
            ? { [`pub_${category.linkedLevel}`]: category.linkedCategoryId }
            : {}),
        }
      : { [`pub_${category.categoryLevelKey}`]: category.marketplaceCategoryId };
  if (scope === 'locL2') {
    params.pub_product_loction = location.enumValue;
  } else if (scope === 'locL1') {
    // Comma-separated enum values: Marketplace API OR-matches multiple
    // values for an enum-schema public data filter.
    const enumValues = location.locL2Slugs
      .map(slug => findSeoLocationBySlug(slug))
      .filter(Boolean)
      .map(l => l.enumValue);
    params.pub_product_loction = enumValues.join(',');
  }
  return params;
};

// ================ Metadata ================ //

const interpolate = (template, values) =>
  Object.entries(values).reduce(
    (str, [key, value]) => str.replace(new RegExp(`{{${key}}}`, 'g'), value),
    template
  );

/**
 * Renders Page Title / H1 / Meta Description / meta keywords for a resolved
 * combo, using the category's own approved template when present, falling
 * back to the generic template (see seoMetadataTemplates.js) otherwise.
 *
 * `metaKeywords` surfaces the category's approved Primary SEO keyword plus
 * its configured Search aliases (SOW Section 3) as a meta keywords tag, so
 * alternative wording is associated with this exact canonical URL instead
 * of minting a separate indexable page for each synonym.
 */
export const renderSeoMetadata = ({ category, location, scope }) => {
  const locationLabel = resolveLocationLabel(scope, location?.label);
  const values = { LOCATION: locationLabel, PRIMARY_KEYWORD: category.primaryKeyword };

  const pageTitleTemplate = category.pageTitleTemplate || genericTemplates.pageTitleTemplate;
  const h1Template = category.h1Template || genericTemplates.h1Template;
  const metaDescriptionTemplate =
    category.metaDescriptionTemplate || genericTemplates.metaDescriptionTemplate;

  const metaKeywords = [category.primaryKeyword, ...(category.aliases || [])]
    .filter(Boolean)
    .join(', ');

  return {
    pageTitle: interpolate(pageTitleTemplate, values),
    h1: interpolate(h1Template, values),
    metaDescription: interpolate(metaDescriptionTemplate, values),
    metaKeywords,
  };
};

/**
 * Full canonical path for a resolved combo, e.g. "/sewa-forklift-jakarta-utara".
 */
export const buildSeoCanonicalPath = ({ category, location, scope }) => {
  const slugPart = categorySlugPart(category);
  if (scope === 'category-only') return `/sewa-${slugPart}`;
  return `/sewa-${slugPart}-${location.slug}`;
};

// ================ Reverse lookup (for /s canonical tag, Phase 5) ================ //

const normalizeKeyword = value => String(value ?? '').trim().toLowerCase();

const resolveLocationScope = locParam => {
  if (!locParam) return { location: null, scope: 'category-only' };

  const values = String(locParam).split(',').sort();
  const jakartaValues = JAKARTA_LOC_L1.locL2Slugs
    .map(slug => findSeoLocationBySlug(slug)?.enumValue)
    .filter(Boolean)
    .sort();
  if (values.length > 1 && values.join(',') === jakartaValues.join(',')) {
    return { location: JAKARTA_LOC_L1, scope: 'locL1' };
  }

  if (values.length === 1) {
    const location = seoLocations.find(l => l.enumValue === values[0]);
    if (location) return { location, scope: 'locL2' };
  }
  return null;
};

// A CAT4 entry has no Sharetribe category id of its own: it is searched by
// `keywords` (its keywordTerm) plus its linked category — see
// buildSeoSearchParams. This is the inverse of that mapping.
const findCat4Category = (searchParams, categories) => {
  const keyword = normalizeKeyword(searchParams.keywords);
  return categories.find(
    c =>
      c.level === 'L4' &&
      normalizeKeyword(c.keywordTerm) === keyword &&
      (!c.linkedCategoryId || searchParams[`pub_${c.linkedLevel}`] === c.linkedCategoryId)
  );
};

const findMarketplaceCategory = (searchParams, categories) => {
  const categoryParam = searchParams.pub_categoryLevel3 ?? searchParams.pub_categoryLevel2;
  if (!categoryParam) return null;
  return categories.find(
    c =>
      c.level !== 'L4' &&
      c.marketplaceCategoryId === categoryParam &&
      (searchParams.pub_categoryLevel3
        ? c.categoryLevelKey === 'categoryLevel3'
        : c.categoryLevelKey === 'categoryLevel2')
  );
};

/**
 * Given the current /s search page's params (pub_categoryLevel2/3,
 * pub_product_loction, and `keywords` for CAT4), returns the matching
 * combo's canonical /sewa-... path, or null if the current params don't
 * correspond to a known SEO combo.
 *
 * A search carrying `keywords` only maps to a CAT4 page (keyword equals the
 * CAT4 keywordTerm and the linked category matches); any other keyword
 * search is not an equivalent of a clean SEO page.
 * @param {Object} searchParams
 * @param {Array} [categories] defaults to the build-time bundled list; pass the
 *   live taxonomy (state.seoPages.seoCategories) so this agrees with routing
 */
export const resolveCanonicalSeoPath = (searchParams, categories = defaultSeoCategories) => {
  const category = normalizeKeyword(searchParams.keywords)
    ? findCat4Category(searchParams, categories)
    : findMarketplaceCategory(searchParams, categories);
  if (!category) return null;

  const resolved = resolveLocationScope(searchParams.pub_product_loction);
  if (!resolved) return null;
  return buildSeoCanonicalPath({ category, ...resolved });
};

// ================ Indexing rules for /s search URLs ================ //

/**
 * Whether a `/s` search page should carry `noindex`. Parameter-based search
 * URLs must not compete with the clean SEO pages, so:
 *  - SEO landing pages themselves (`seoOverrides` present) are indexable;
 *  - a `/s` URL without any query string is indexable (it's in the default sitemap);
 *  - a `/s?...` URL whose category/location maps to a currently eligible
 *    `/sewa-...` page is indexable-by-canonical only: it gets the canonical
 *    tag pointing to the clean URL (see canonicalRoutePath) and NOT noindex,
 *    because noindex + canonical-to-another-URL sends conflicting signals;
 *  - every other `/s?...` URL gets `noindex`.
 *
 * @param {Object} params
 * @param {string} params.search - location.search of the page
 * @param {Array<string>} params.eligiblePaths - currently eligible /sewa-... paths
 * @param {boolean} [params.isSeoLandingPage]
 * @param {Array} [params.categories] defaults to the build-time bundled list
 * @returns {boolean}
 */
export const shouldNoIndexSearchPage = ({
  search,
  eligiblePaths = [],
  isSeoLandingPage,
  categories = defaultSeoCategories,
}) => {
  if (isSeoLandingPage) return false;

  const searchParams = new URLSearchParams(search || '');
  if ([...searchParams.keys()].length === 0) return false;

  const seoPath = resolveCanonicalSeoPath(Object.fromEntries(searchParams.entries()), categories);
  return !(seoPath && eligiblePaths.includes(seoPath));
};

// ================ Internal SEO links ================ //

const toLink = (path, combo) => ({
  path,
  seoSlug: path.replace(/^\/sewa-/, ''),
  label: renderSeoMetadata(combo).h1,
  combo,
});

const resolveEligibleLinks = (eligiblePaths, categories) =>
  eligiblePaths
    .map(path => {
      const combo = resolveSeoSlug(path.replace(/^\/sewa-/, ''), categories);
      return combo ? toLink(path, combo) : null;
    })
    .filter(Boolean);

const byLabel = (a, b) => a.label.localeCompare(b.label);

/**
 * Links for the site-wide footer block: category-only eligible pages
 * (alphabetical, capped). Falls back to any eligible page if no
 * category-only page is live yet.
 *
 * @param {Array<string>} eligiblePaths
 * @param {Array} [categories] defaults to the build-time bundled list
 * @param {number} [max]
 * @returns {Array<{path: string, seoSlug: string, label: string}>}
 */
export const pickFooterSeoLinks = (eligiblePaths = [], categories = defaultSeoCategories, max = 24) => {
  const links = resolveEligibleLinks(eligiblePaths, categories);
  const categoryOnly = links.filter(l => l.combo.scope === 'category-only');
  return (categoryOnly.length > 0 ? categoryOnly : links)
    .sort(byLabel)
    .slice(0, max)
    .map(({ combo, ...link }) => link);
};

/**
 * Links for a given SEO page: other eligible locations of the same category
 * first, then the same location for other categories. Excludes the page itself.
 *
 * @param {Array<string>} eligiblePaths
 * @param {string} currentPath - e.g. "/sewa-forklift-jakarta-utara"
 * @param {Array} [categories] defaults to the build-time bundled list
 * @param {number} [max]
 * @returns {Array<{path: string, seoSlug: string, label: string}>}
 */
export const pickRelatedSeoLinks = (
  eligiblePaths = [],
  currentPath,
  categories = defaultSeoCategories,
  max = 12
) => {
  const current = resolveSeoSlug((currentPath || '').replace(/^\/sewa-/, ''), categories);
  if (!current) return [];

  const others = resolveEligibleLinks(eligiblePaths, categories).filter(
    l => l.path !== currentPath
  );
  const sameCategory = others
    .filter(l => l.combo.category.canonicalSlug === current.category.canonicalSlug)
    .sort(byLabel);
  const sameLocation =
    current.scope === 'category-only'
      ? []
      : others
          .filter(
            l =>
              l.combo.category.canonicalSlug !== current.category.canonicalSlug &&
              l.combo.location?.slug === current.location.slug
          )
          .sort(byLabel);

  return [...sameCategory, ...sameLocation].slice(0, max).map(({ combo, ...link }) => link);
};
